import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { NavLink, useNavigate } from 'react-router-dom';

// material ui
import {
    Box,
    CardContent,
    Divider,
    Grid,
    InputAdornment,
    IconButton,
    OutlinedInput,
    Typography,
    TextField,
    Autocomplete,
    Tooltip,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import AddCircleTwoTone from '@mui/icons-material/AddCircleTwoTone';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';

//import projects
import MainCard from 'ui-component/cards/MainCard';
import { setConversation, updateConversationId } from './conversationSlice';
import ConversationTile from './ConversationTile';
import { store } from 'store';
import apiSlice from 'store/slices/apiSlice';
import SubCard from 'ui-component/cards/SubCard';
import { sendFeedback } from './conversationApi';
import ConversationBubble from './ConversationBubble';
import { addQuery, fetchAnswer, selectQueries, selectStatus, updateQuery } from './conversationSlice';

//react-redux
import { useDispatch, useSelector } from 'react-redux';
import { selectConversations, setConversations } from './preferences/preferenceSlice';
import { openSnackbar } from 'store/slices/snackbar';

//styles
import { useTheme } from '@mui/material/styles';
import './index.css';
//third party
import ClipLoader from 'react-spinners/ClipLoader';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MicIcon from '@mui/icons-material/Mic';
import MicRecorder from 'mic-recorder-to-mp3';
import { useSpeechRecognition } from 'react-speech-recognition';

//internal variant
const project = 'chatgpt';

export default function Conversation() {
    //config
    const { isMobile } = useMediaQuery();
    const embeddingsName = 'openai_text-embedding-ada-002';
    const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    //theme
    const theme = useTheme();

    //ref
    const endMessageRef = useRef(null);
    const inputRef = useRef(null);
    const navRef = useRef(null);
    const modelRef = useRef();
    const scrollRefChat = useRef();

    //state for container
    const [message, setMessage] = useState('');
    const [scrollEl, setScrollEl] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    const [model, setModel] = useState({ name: 'gpt-4', label: 'gpt-4' });
    const [inputModel, setInputModel] = useState('gpt-4');
    const [blobURL, setBlobURL] = useState('');
    const [isBlock, setIsBlock] = useState(false);
    const [recorder, setRecoder] = useState(
        new MicRecorder({
            bitRate: 128
        })
    );
    const [isMicro, setIsMicro] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [openModelChangeDialog, setOpenModelChangeDialog] = useState(false);

    //state redux
    const queries = useSelector(selectQueries);
    const status = useSelector(selectStatus);
    const conversations = useSelector(selectConversations);

    //UseEffect - prepare data when first time enter
    useEffect(() => {
        if (!browserSupportsSpeechRecognition) {
            setIsBlock(true);
            // return <span>Browser doesn't support speech recognition.</span>;
        }
    }, []);

    useEffect(() => {
        dispatch(setConversation([])); // Return an empty conversation when the topic changes
        dispatch(
            updateConversationId({
                query: { conversationId: null }
            })
        );
    }, []);

    useEffect(() => {
        fetchConversations(project);
    }, []);

    useEffect(() => {
        if (!conversations) {
            fetchConversations(project);
        }
    }, [conversations, dispatch]);

    async function fetchConversations(project) {
        try {
            const res = await store.dispatch(
                apiSlice.endpoints.getConversationsList.initiate({
                    project,
                    user_token: localStorage.getItem('user_token')
                })
            );
            if (res.data) dispatch(setConversations(res.data));
        } catch (error) {
            console.error('Failed to fetch conversations: ', error);
        }
    }

    //Actions
    const handleQuestion = (question, model) => {
        question = question.trim();
        // console.log(question);
        // console.log(model['name'])
        if (question === '') return;
        dispatch(addQuery({ prompt: question }));
        dispatch(fetchAnswer({ question, model: model, project }));
    };

    const handleFeedback = (query, feedback, index) => {
        const prevFeedback = query.feedback;
        dispatch(updateQuery({ index, query: { feedback } }));
        sendFeedback(query.prompt, query.response, feedback).catch(() => {
            dispatch(updateQuery({ index, query: { feedback: prevFeedback } }));
        });
    };

    const handleConversationClick = (index) => {
        store
            .dispatch(apiSlice.endpoints.getConversation.initiate(index))
            .then((res) => {
                if (!res.data) return;
                navigate('/workspace/' + project);
                dispatch(setConversation(res.data));
                dispatch(
                    updateConversationId({
                        query: { conversationId: index }
                    })
                );
            });
    };

    async function updateConversationName(updatedConversation) {
        try {
            const res = await store.dispatch(
                apiSlice.endpoints.updateConversation.initiate({
                    id: updatedConversation.id,
                    ...updatedConversation
                })
            );
            if (res?.data) {
                navigate('/workspace/' + project);
                fetchConversations(project);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleDeleteConversation = (id) => {
        store
            .dispatch(apiSlice.endpoints.deleteConversation.initiate(id))
            .then(() => {
                fetchConversations(project);
                dispatch(setConversation([]));
                dispatch(
                    updateConversationId({
                        query: { conversationId: null }
                    })
                );
                navigate('/workspace/' + project);
            })
            .catch((error) => console.error(error));
    };

    const prepResponseView = (query, index) => {
        let responseView;
        if (query.error) {
            responseView = (
                <ConversationBubble
                    ref={endMessageRef}
                    className={`${index === queries.length - 1 ? 'mb-2' : 'mb-2'}`}
                    key={`${index}ERROR`}
                    message={query.error}
                    type="ERROR"
                ></ConversationBubble>
            );
        } else if (query.response) {
            responseView = (
                <ConversationBubble
                    ref={endMessageRef}
                    className={`${index === queries.length - 1 ? 'mb-2' : 'mb-2'}`}
                    key={`${index}ANSWER`}
                    message={query.response}
                    type={'ANSWER'}
                    sources={query.sources}
                    feedback={query.feedback}
                    handleFeedback={(feedback) => handleFeedback(query, feedback, index)}
                    isTheLast={index === queries.length - 1}
                ></ConversationBubble>
            );
        }
        return responseView;
    };

    async function callWhisper(file) {
        // console.log('before call');
        const fileUri = URL.createObjectURL(file);
        const formData = new FormData();
        // formData.append('model', 'whisper-1');
        formData.append('file', file);
        formData.append('api_key', 'kspwKCsQoYJA6GSH4ydKYjdyGveQzMiDpinxAOrEw');
        // formData.append('response_format', 'text');
        // const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        //     method: 'POST',
        //     body: formData,
        //     headers: {
        //         Authorization: 'Bearer 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'
        //     }
        // });

        const res = await store.dispatch(apiSlice.endpoints.postS2tWhisper.initiate(formData));
        if (res.error) throw new Error(res.error?.data?.detail || 'S2T failed');
        const text = res.data || {};
        // console.log(text);
        setMessage(text['answer']);
        // console.log('callChatGPTRes', res.status);
        // console.log('callChatGPTRes', text);
        return text['answer'];
    }

    const start = () => {
        // console.log(recorder);

        if (isBlock || !recorder) {
            console.log('Permission Denied or Recorder not initialized');
        } else {
            recorder
                .start()
                .then(() => {
                    setIsRecording(true);
                })
                .catch((e) => console.error(e));
        }
    };

    const stop = async () => {
        if (isRecording === true && recorder) {
            try {
                const [buffer, blob] = await recorder.stop().getMp3();
                const file = new File(buffer, 'speech.mp3', {
                    type: blob.type,
                    lastModified: Date.now()
                });
                // console.log(file);
                const blobUrl = URL.createObjectURL(file);
                // console.log(blobUrl);
                await callWhisper(file, file.name);

                setIsRecording(false);
            } catch (e) {
                console.log(e);
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleModelChange = (e, model) => {
        modelRef.current = model;
        setOpenModelChangeDialog(true);
    };

    const handleSubmitModelChange = () => {
        // get project storage
        setModel(modelRef.current);
        setOpenModelChangeDialog(false);
        dispatch(
            openSnackbar({
                open: true,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                message: 'Cập nhật Model thành công',
                variant: 'alert',
                alert: {
                    color: 'success'
                }
            })
        );
    };

    const handleCloseModelChange = () => {
        setOpenModelChangeDialog(false);
    };

    return (
        <MainCard
            className="h-100"
            contentSX={{ padding: 0 }}
            headersx={{ padding: '10px' }}
            sx={{
                bgcolor: 'white'
            }}
            style={{
                minHeight: 'calc(100vh-80px)'
            }}
        >
            <Grid container spacing={0} sx={{ padding: 0 }}>
                <Grid item xs={12}>
                    <Grid container sx={{ justifyContent: 'space-between' }}>
                        <Grid container>
                            <Grid item xs={3} padding={1}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    id="combo-box-mo-hinh"
                                    // openOnFocus
                                    getOptionLabel={(option) => option?.label}
                                    isOptionEqualToValue={(option, value) => option?.name !== value?.name}
                                    options={[
                                        { name: 'gpt-4', label: 'gpt-4' },
                                        { name: 'gpt-4-turbo', label: 'gpt-4-turbo' },
                                        { name: 'gpt-4o', label: 'gpt-4o' },
                                        { name: 'gpt-4o-mini', label: 'gpt-4o-mini' },
                                        // { name: 'gemini', label: 'gemini' },
                                        // { name: 'gemini-pro', label: 'gemini-pro' },
                                        // { name: 'gpt-4-0613', label: 'gpt-4-0613' },
                                        // { name: 'gpt-4-32k', label: 'gpt-4-32k' },
                                        // { name: 'gpt-4-32k-0613', label: 'gpt-4-32k-0613' },
                                        // {"name": "gpt-4-turbo", "label": "gpt-4-turbo"},
                                        // {"name": "gpt-3-turbo","label":"gpt-3-turbo"},
                                        // {"name": "gpt-3-turbo-0613","label":"gpt-3-turbo-0613"},
                                        // {"name": "gpt-3-turbo-16k","label":"gpt-3-turbo-16k"},
                                        // {"name": "gpt-3-turbo-16k-0613","label":"gpt-3-turbo-16k-0613"},
                                        // {"name": "gpt-3-turbo-long","label":"gpt-3-turbo-long"},
                                        // {"name": "code-davinci-002","label":"code-davinci-002"},
                                        // {"name": "text-ada-001","label":"text-ada-001"},
                                        // {"name": "text-babbage-001","label":"text-babbage-001"},
                                        // {"name": "text-curie-001","label":"text-curie-001"},
                                        // {"name": "text-davinci-002","label":"text-davinci-002"},
                                        // {"name": "text-davinci-003","label":"text-davinci-003"},
                                        { name: 'gpt-4-1106', label: 'gpt-4-1106 (trả phí)' }
                                    ]}
                                    getOptionDisabled={(option) =>
                                        option.name === 'gpt-4-1106' && localStorage.getItem('user_type') === 'trial'
                                    }
                                    renderInput={(params) => <TextField {...params} id="mo-hinh" label="Chọn mô hình" />}
                                    value={model}
                                    onChange={(e, model) => {
                                        handleModelChange(e, model);
                                    }}
                                    inputValue={inputModel}
                                    onInputChange={(e, newInputModel) => {
                                        setInputModel(newInputModel);
                                    }}
                                />
                                <Dialog
                                    open={openModelChangeDialog}
                                    onClose={handleCloseModelChange}
                                    aria-labelledby="model-dialog-title"
                                    aria-describedby="model-dialog-description"
                                >
                                    <DialogTitle id="model-dialog-title">{'Bạn muốn chọn model này ?'}</DialogTitle>
                                    <DialogContent>
                                        <DialogContentText id="alert-dialog-description">
                                            Chọn model khác sẽ ảnh hưởng đến cuộc trò chuyện của bạn!
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseModelChange} variant="outlined" color="secondary">
                                            Thoát
                                        </Button>
                                        <Button
                                            onClick={handleSubmitModelChange}
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#156DA2'
                                            }}
                                        >
                                            Đồng ý
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider sx={{ mt: theme.spacing(1) }} />
                </Grid>
                <Grid item xs={3} sx={{ height: '100%', overflowY: 'auto', padding: '0px' }}>
                    <SubCard
                        sx={{ padding: 0, overflowY: 'auto' }}
                        headersx={{ textAlign: 'center' }}
                        title={
                            <Grid item xs={12} textAlign="center">
                                <NavLink to={'/workspace/' + project}>
                                    <Button
                                        variant="contained"
                                        sx={{ padding: 1.8, paddingX: 6, backgroundColor: '#156DA2' }}
                                        startIcon={<AddCircleTwoTone />}
                                        onClick={() => {
                                            dispatch(setConversation([]));
                                            dispatch(
                                                updateConversationId({
                                                    query: { conversationId: null }
                                                })
                                            );
                                        }}
                                    >
                                        Tin nhắn mới
                                    </Button>
                                </NavLink>
                            </Grid>
                        }
                    >
                        {/* <Navigation navOpen={navOpen} setNavOpen={setNavOpen} /> */}
                        <Typography variant="h5">Lịch sử tin nhắn</Typography>
                        <PerfectScrollbar
                            style={{
                                width: '100%',
                                height: 'calc(100vh - 320px)',
                                overflowX: 'hidden'
                                // minHeight: 'calc(100vh - 280px)'
                            }}
                            containerRef={(ref) => {
                                setScrollEl(ref);
                            }}
                        >
                            {conversations && (
                                // <div className="conversations-container mb-auto max-h-[25rem] overflow-y-auto">
                                <div>
                                    {conversations?.map((conversation) => (
                                        <ConversationTile
                                            key={conversation.id}
                                            conversation={conversation}
                                            selectConversation={(id) => handleConversationClick(id)}
                                            onDeleteConversation={(id) => handleDeleteConversation(id)}
                                            onSave={(conversation) => updateConversationName(conversation)}
                                        />
                                    ))}
                                </div>
                            )}
                            {!conversations && <Typography variant="body1">Chưa có cuộc trò chuyện nào</Typography>}
                        </PerfectScrollbar>
                    </SubCard>
                </Grid>
                <Grid item xs={9} sx={{ padding: '0px' }}>
                    <SubCard>
                        <Grid container>
                            {/* <Grid item xs={12}>
                                <div className="min-h-full min-w-full">
                                    <div
                                        className={`transition-all duration-200 ${
                                            !isMobile ? `ml-0 ${!navOpen ? '-mt-5 md:mx-auto lg:mx-auto' : 'md:ml-72'}` : 'ml-0 md:ml-16'
                                        }`}
                                    >
                                        <div className="flex flex-col justify-center bg-white p-4  md:flex-row">
                                            {queries.length > 0 && !hasScrolledToLast && (
                                                <button
                                                    onClick={scrollIntoView}
                                                    aria-label="scroll to bottom"
                                                    className="fixed bottom-32 right-14 z-10 flex h-7 w-7  items-center justify-center rounded-full border-[0.5px] border-gray-alpha bg-gray-100 bg-opacity-50 md:h-9 md:w-9 md:bg-opacity-100 "
                                                >
                                                    <img src={ArrowDown} alt="arrow down" className="h4- w-4 opacity-50 md:h-5 md:w-5" />
                                                </button>
                                            )}

                                            {queries.length > 0 && (
                                                <div className="mt-20 flex flex-col bg-white transition-all  md:w-3/4">
                                                    {queries.map((query, index) => {
                                                        return (
                                                            <Fragment key={index}>
                                                                <ConversationBubble
                                                                    className={'last:mb-27 mb-7'}
                                                                    key={`${index}QUESTION`}
                                                                    message={query.prompt}
                                                                    type="QUESTION"
                                                                    sources={query.sources}
                                                                ></ConversationBubble>
                                                                {prepResponseView(query, index)}
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {queries.length === 0 && <Hero className="mt-24 h-[100vh] md:mt-52"></Hero>}
                                            <div className="relative bottom-0 flex w-10/12 flex-col items-end self-center pt-3 md:fixed md:w-[65%]">
                                                <div className="flex h-full w-full" style={{ paddingBottom: '30px' }}>
                                                    <div
                                                        id="inputbox"
                                                        ref={inputRef}
                                                        tabIndex={1}
                                                        placeholder="Type your message here..."
                                                        contentEditable
                                                        onPaste={handlePaste}
                                                        className={`border-000000 overflow-x-hidden; max-h-24 min-h-[2.6rem] w-full overflow-y-auto whitespace-pre-wrap rounded-3xl border bg-white py-2 pl-4 pr-9 text-base leading-7 opacity-100 focus:outline-none`}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                if (inputRef.current?.textContent && status !== 'loading') {
                                                                    handleQuestion(inputRef.current.textContent);
                                                                    inputRef.current.textContent = '';
                                                                }
                                                            }
                                                        }}
                                                    ></div>
                                                    {status === 'loading' ? (
                                                        <img
                                                            src={Spinner}
                                                            className="relative bottom-[7px] right-[38px] -mr-[30px] animate-spin cursor-pointer self-end"
                                                        ></img>
                                                    ) : (
                                                        <div className="relative bottom-[7px] right-[43px] -mr-[35px] h-[35px] w-[35px] cursor-pointer self-end rounded-full hover:bg-gray-3000">
                                                            <img
                                                                className="ml-[9px] mt-[9px]"
                                                                onClick={() => {
                                                                    if (inputRef.current?.textContent) {
                                                                        handleQuestion(inputRef.current.textContent);
                                                                        inputRef.current.textContent = '';
                                                                    }
                                                                }}
                                                                src={Send}
                                                            ></img>
                                                        </div>
                                                    )}
                                                </div> */}
                            {/* <p className="text-gray-595959 w-[100vw] self-center bg-white p-5 text-center text-xs md:w-full"></p> */}
                            {/* </div>
                                        </div>
                                    </div>
                                </div>
                            </Grid> */}
                            <PerfectScrollbar
                                style={{
                                    width: '100%',
                                    height: 'calc(100vh - 400px)',
                                    overflowX: 'hidden',
                                    minHeight: 'calc(100vh - 250px)'
                                }}
                                containerRef={(ref) => {
                                    setScrollEl(ref);
                                }}
                            >
                                <Box>
                                    <CardContent>
                                        {/* {queries.length > 0 && !hasScrolledToLast && (
                                        <button
                                            onClick={scrollIntoView}
                                            aria-label="scroll to bottom"
                                            className="fixed bottom-32 right-14 z-10 flex h-7 w-7  items-center justify-center rounded-full border-[0.5px] border-gray-alpha bg-gray-100 bg-opacity-50 md:h-9 md:w-9 md:bg-opacity-100 "
                                        >
                                            <img src={ArrowDown} alt="arrow down" className="h4- w-4 opacity-50 md:h-5 md:w-5" />
                                        </button>
                                    )} */}
                                        <Box sx={{ p: 3, pt: 0 }}>
                                            {queries.length > 0 && (
                                                <Grid item xs={12}>
                                                    {queries.map((query, index) => {
                                                        return (
                                                            <Box key={index}>
                                                                <ConversationBubble
                                                                    className={'mb-2'}
                                                                    key={`${index}QUESTION`}
                                                                    message={query.prompt}
                                                                    type="QUESTION"
                                                                    sources={query.sources}
                                                                ></ConversationBubble>
                                                                {prepResponseView(query, index)}
                                                            </Box>
                                                        );
                                                    })}
                                                    <span ref={scrollRefChat} />
                                                </Grid>
                                            )}
                                            {queries.length === 0 && (
                                                <ConversationBubble
                                                    ref={endMessageRef}
                                                    className="mb-2"
                                                    message="Xin chào, Tôi có thể giúp gì cho bạn hôm nay? 😊"
                                                    type={'GREETING'}
                                                    // sources={query.sources}
                                                    // feedback={query.feedback}
                                                    // handleFeedback={(feedback) => handleFeedback(query, feedback, index)}
                                                    // isTheLast={index === queries.length - 1}
                                                ></ConversationBubble>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Box>
                            </PerfectScrollbar>
                            <Grid container alignItems="center">
                                <Grid item xs={11} padding={0}>
                                    <OutlinedInput
                                        multiline={true}
                                        size="small"
                                        ref={inputRef}
                                        tabIndex={1}
                                        fullWidth
                                        autoFocus={false}
                                        // contentEditable
                                        value={isRecording === true ? 'Đang nghe...' : message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        // onPaste={handlePaste}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                if (message && status !== 'loading') {
                                                    handleQuestion(message, model.name);
                                                    setMessage('');
                                                }
                                            }
                                        }}
                                        placeholder="Nhập tin nhắn của bạn ở đây..."
                                        endAdornment={
                                            <InputAdornment position="end">
                                                {isMicro && isRecording === false && (
                                                    <Tooltip title="Sử dụng micro" placement="top" arrow>
                                                        <IconButton
                                                            disableRipple
                                                            onClick={() => start()}
                                                            // color="primary"
                                                            // onClick={(e) => {
                                                            //     if (e.target.value) {
                                                            //         handleQuestion(e.target.value);
                                                            //         e.target.value = '';
                                                            //     }
                                                            // }}
                                                            aria-label="use micro"
                                                        >
                                                            <MicIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {isMicro && isRecording === true && (
                                                    <Tooltip title="Đã nói xong rồi" placement="top" arrow>
                                                        <IconButton
                                                            disabled={status === 'loading'}
                                                            onClick={() => stop()}
                                                            // color="primary"
                                                            // onClick={(e) => {
                                                            //     if (e.target.value) {
                                                            //         handleQuestion(e.target.value);
                                                            //         e.target.value = '';
                                                            //     }
                                                            // }}
                                                            aria-label="use micro"
                                                        >
                                                            <StopCircleIcon color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </InputAdornment>
                                        }
                                        aria-describedby="search-helper-text"
                                        inputProps={{ 'aria-label': 'weight' }}
                                    />
                                </Grid>
                                <Grid item xs={1} sx={{ paddingLeft: '8px' }}>
                                    {message === '' && status !== 'loading' && (
                                        <IconButton disabled color="primary" aria-label="send message">
                                            <SendTwoToneIcon />
                                        </IconButton>
                                    )}
                                    {status === 'loading' && <ClipLoader color="#156DA2" />}
                                    {message !== '' && status !== 'loading' && (
                                        <Tooltip title="Gửi tin nhắn" placement="top" arrow>
                                            <IconButton
                                                color="primary"
                                                onClick={(e) => {
                                                    if (message && status !== 'loading') {
                                                        handleQuestion(message, model);
                                                        setMessage('');
                                                    }
                                                }}
                                                aria-label="send message"
                                            >
                                                <SendTwoToneIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>
        </MainCard>
    );
}
